// ---------------------------------------------------------------------------
// Media Library store — one grid over two sources of truth:
//   1. TASK-DERIVED media: every attachment already living on a task.
//   2. LIBRARY uploads: files sent straight to the library, addressed to
//      "Everyone at CG" or to one project's audience.
// Permission trimming and filtering happen SERVER-side (the client never sees
// items just to hide them, and `total` stays honest for paging) — this store
// only holds the current page and the upload pipeline.
// ---------------------------------------------------------------------------

import { create } from 'zustand';
import type { MediaItem, MediaKind, MediaScope, MediaListResponse } from '@cg-projects/shared';
import { api, getAuthToken, CLIENT_ID } from '@/core/api/apiClient';
import { useToastStore } from '@/core/ui/toastStore';

export const PAGE_SIZE = 60;
/** Library uploads take bigger files than task attachments (walkthrough videos,
 *  raw photo sets) — checked client-side before any bytes move. */
export const MAX_UPLOAD_BYTES = 150 * 1024 * 1024;

// Same resolution apiClient uses. Its base is module-private, and the upload
// below is the one call that can't go through request() — raw bytes, not JSON.
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002/api/v1';

/** "1.4 MB" / "312 KB" — sizes are meta, not math; one decimal is plenty. */
export function humanSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes >= 1_073_741_824) return `${(bytes / 1_073_741_824).toFixed(1)} GB`;
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

export interface MediaFilter {
  /** null = every kind. */
  kind: MediaKind | null;
  /** null = all projects. */
  areaId: number | null;
  q: string;
  scope: MediaScope;
}

export interface PendingUpload {
  key: string;
  name: string;
  size: number;
  status: 'uploading' | 'error';
  /** Human reason, only when status === 'error'. */
  message?: string;
}

interface MediaState {
  /** null = never loaded — the boot skeletons' cue. Kept (not nulled) across
   *  filter changes so the grid dims instead of flashing empty. */
  items: MediaItem[] | null;
  /** Server count for the CURRENT filters after permission trimming. */
  total: number;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  filter: MediaFilter;
  uploads: PendingUpload[];
  /** Timestamp signal → UploadZone opens its file picker. A timestamp, not a
   *  boolean, so every press is a distinct signal (mirrors uiStore.composerRequest). */
  pickerRequest: number;
  requestPicker: () => void;
  /** Fetch page one for the current filters (stale-while-revalidate: existing
   *  items stay on screen until the answer lands). */
  load: () => Promise<void>;
  loadMore: () => Promise<void>;
  /** Merge a filter change and refetch. */
  setFilter: (patch: Partial<MediaFilter>) => void;
  /** Upload sequentially; each success lands in the grid optimistically (when it
   *  matches the current filters), each failure stays in the strip + toasts. */
  uploadFiles: (files: File[], audienceAreaId: number | null) => Promise<void>;
  /** Server-first delete (the server is the real permission gate — 403 toasts).
   *  Resolves true when the item is gone. */
  remove: (id: number) => Promise<boolean>;
  dismissUpload: (key: string) => void;
}

// Out-of-order guard: filters change faster than Odoo answers, and a slow
// "All" response must not overwrite a fast "Videos" one.
let loadSeq = 0;

function queryFor(filter: MediaFilter, offset: number): string {
  const p = new URLSearchParams();
  if (filter.kind) p.set('kind', filter.kind);
  if (filter.areaId !== null) p.set('areaId', String(filter.areaId));
  if (filter.q.trim()) p.set('q', filter.q.trim());
  if (filter.scope !== 'all') p.set('scope', filter.scope);
  p.set('limit', String(PAGE_SIZE));
  p.set('offset', String(offset));
  return p.toString();
}

/** Would this (just-uploaded) item appear under the current filters? Inserting a
 *  video into the Images tab would make the filter a liar — mismatches stay out
 *  of the grid and get a toast instead. */
function matchesFilter(item: MediaItem, f: MediaFilter): boolean {
  if (f.kind && item.kind !== f.kind) return false;
  if (f.scope === 'library' && !item.isLibrary) return false;
  if (f.areaId !== null) {
    const itemArea = item.isLibrary ? item.audienceAreaId : item.areaId;
    if (itemArea !== f.areaId) return false;
  }
  const q = f.q.trim().toLowerCase();
  if (q && !item.name.toLowerCase().includes(q)) return false;
  return true;
}

/** Raw-body upload per the media contract. apiClient.request() is JSON-only, so
 *  this is a sibling using the SAME auth token + client id + defensive body
 *  parse (a proxy 413 answers with an HTML page, not JSON — see apiClient). */
async function rawUpload(file: File, audienceAreaId: number | null): Promise<MediaItem> {
  const controller = new AbortController();
  // Generous — 150 MB over store wifi is slow — but bounded, or a stalled
  // connection pins an "uploading…" row forever.
  const timer = setTimeout(() => controller.abort(), 600_000);
  try {
    const token = getAuthToken();
    const res = await fetch(`${API_BASE_URL}/media`, {
      method: 'POST',
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        'x-cg-name': encodeURIComponent(file.name),
        'x-cg-mimetype': file.type || 'application/octet-stream',
        'x-cg-audience': String(audienceAreaId ?? 0),
        'X-Client-Id': CLIENT_ID,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: file,
      signal: controller.signal,
    });
    const raw = await res.text();
    type UploadReply = { success?: boolean; data?: { item: MediaItem }; error?: { message?: string } };
    let data: UploadReply | null = null;
    // `as typeof data` would query the variable's NARROWED type here (null, it was
    // just initialised) and collapse every later read to `never` — hence the alias.
    try { data = raw ? (JSON.parse(raw) as UploadReply) : null; } catch { data = null; }
    if (!res.ok || !data?.success || !data.data?.item) {
      const fallback = res.status === 413
        ? 'That file is too big for the server — try one under 150 MB.'
        : `Upload failed (HTTP ${res.status}).`;
      throw new Error(data?.error?.message ?? fallback);
    }
    return data.data.item;
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error('The upload took too long — check the connection and try again.');
    }
    throw err instanceof Error ? err : new Error('Upload failed.');
  } finally {
    clearTimeout(timer);
  }
}

export const useMediaStore = create<MediaState>()((set, get) => ({
  items: null,
  total: 0,
  loading: false,
  loadingMore: false,
  error: null,
  filter: { kind: null, areaId: null, q: '', scope: 'all' },
  uploads: [],
  pickerRequest: 0,
  requestPicker: () => set({ pickerRequest: Date.now() }),

  load: async () => {
    const at = ++loadSeq;
    set({ loading: true, error: null });
    try {
      const r = await api.get<MediaListResponse>(`/media?${queryFor(get().filter, 0)}`);
      if (loadSeq !== at) return; // a newer filter's answer owns the grid now
      set({ items: r.items, total: r.total, loading: false, error: null });
    } catch (err) {
      if (loadSeq !== at) return;
      set({ loading: false, error: err instanceof Error ? err.message : 'Could not load the media library.' });
    }
  },

  loadMore: async () => {
    const { items, loading, loadingMore, filter } = get();
    if (!items || loading || loadingMore) return;
    const at = loadSeq; // NOT ++: a filter change mid-page-load must win
    set({ loadingMore: true });
    try {
      const r = await api.get<MediaListResponse>(`/media?${queryFor(filter, items.length)}`);
      if (loadSeq !== at) return;
      // Dedupe by id: an optimistic upload insert can shift the server's offset
      // window, and a doubled tile reads as a data bug.
      const seen = new Set((get().items ?? []).map((i) => i.id));
      set((s) => ({
        items: [...(s.items ?? []), ...r.items.filter((i) => !seen.has(i.id))],
        total: r.total,
        loadingMore: false,
      }));
    } catch (err) {
      if (loadSeq !== at) return;
      set({ loadingMore: false });
      useToastStore.getState().show(err instanceof Error ? err.message : 'Could not load more.', 'error');
    }
  },

  setFilter: (patch) => {
    set((s) => ({ filter: { ...s.filter, ...patch } }));
    void get().load();
  },

  uploadFiles: async (files, audienceAreaId) => {
    let hidden = 0;
    for (const [i, file] of files.entries()) {
      const key = `${Date.now()}-${i}-${file.name}`;
      set((s) => ({ uploads: [...s.uploads, { key, name: file.name, size: file.size, status: 'uploading' }] }));
      try {
        const item = await rawUpload(file, audienceAreaId);
        const st = get();
        const insert = st.items !== null && matchesFilter(item, st.filter);
        if (!insert) hidden++;
        set((s) => ({
          uploads: s.uploads.filter((u) => u.key !== key),
          ...(insert && s.items !== null
            ? { items: [item, ...s.items.filter((x) => x.id !== item.id)], total: s.total + 1 }
            : {}),
        }));
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed.';
        set((s) => ({ uploads: s.uploads.map((u) => (u.key === key ? { ...u, status: 'error' as const, message } : u)) }));
        useToastStore.getState().show(`${file.name}: ${message}`, 'error');
      }
    }
    // The upload worked but the current filters hide it — say so, or "I uploaded
    // it and it isn't there" becomes a support thread.
    if (hidden > 0) {
      useToastStore.getState().show(
        hidden === 1 ? 'Uploaded — not shown under the current filters.' : `Uploaded ${hidden} files — not all match the current filters.`,
        'info',
      );
    }
  },

  remove: async (id) => {
    try {
      await api.delete<{ deleted: boolean }>(`/media/${id}`);
      set((s) => ({
        items: s.items ? s.items.filter((i) => i.id !== id) : s.items,
        total: Math.max(0, s.total - 1),
      }));
      return true;
    } catch (err) {
      // 403 (not yours), 409 (MEDIA_POSTGRES_ONLY) and the rest all carry a
      // server-written message — surface it, the button was only a heuristic.
      useToastStore.getState().show(err instanceof Error ? err.message : 'Could not delete.', 'error');
      return false;
    }
  },

  dismissUpload: (key) => set((s) => ({ uploads: s.uploads.filter((u) => u.key !== key) })),
}));
